import {
    createContext,
    useContext,
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type Ref,
} from 'react';
import { RiArrowDropDownLine } from '@remixicon/react';

import { classNames } from '../../internal/classNames';
import { useLinkComponent } from '../../internal/LinkProvider';

import Divider from '../Divider/Divider';
import Icon, { type IconSource } from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';

export type ButtonAction = 'primary' | 'negative' | 'positive';
export type ButtonVariant = 'filled' | 'filled-light' | 'floating';
export type ButtonSize = 'medium' | 'large';

/** Default `size` for every `Button` below it; an explicit `size` prop wins. */
export const ButtonSizeContext = createContext<ButtonSize | undefined>(undefined);

type ButtonCommonProps = {
    action?: ButtonAction;
    processing?: boolean;
    variant?: ButtonVariant;
    icon?: IconSource;
    size?: ButtonSize;
};

type ButtonWithText = {
    children: ReactNode;
    /** Optional tooltip; not required when the button has a label. */
    title?: string;
};

type IconOnlyButton = {
    children?: undefined | null;
    /** Required for icon-only buttons (tooltip + accessible name). */
    title: string;
};

type ButtonContentRule = ButtonWithText | IconOnlyButton;

export type ButtonButtonProps = ButtonCommonProps &
    ButtonContentRule &
    Omit<ComponentPropsWithRef<'button'>, 'children' | 'title'> & {
        href?: undefined;
        ref?: Ref<HTMLButtonElement>;
    };

/** Renders through the host `LinkProvider` component; disabled or processing links render a native `<a>` without `href`. */
export type ButtonLinkProps = ButtonCommonProps &
    ButtonContentRule &
    Omit<ComponentPropsWithRef<'a'>, 'children' | 'title'> & {
        href: string;
        disabled?: boolean;
        ref?: Ref<HTMLAnchorElement>;
    };

export type ButtonProps = ButtonButtonProps | ButtonLinkProps;

function renderLeadingIcon(
    processing: boolean,
    icon: IconSource | undefined,
): ReactNode | undefined {
    const iconSource: IconSource | undefined = processing ? 'loading' : icon;
    if (!iconSource) {
        return undefined;
    }
    return (
        <Icon
            icon={iconSource}
            size={16}
            className='ui-button__icon'
            aria-hidden
        />
    );
}

function Button(props: ButtonProps) {
    const Link = useLinkComponent();
    const contextSize = useContext(ButtonSizeContext);
    const {
        variant = 'filled',
        action = 'primary',
        processing = false,
        size = contextSize ?? 'large',
        icon,
        children,
        disabled = false,
        className = '',
        title,
        'aria-label': ariaLabel,
        ...rest
    } = props;
    const iconOnly = children === undefined || children === null;
    const leadingIcon = renderLeadingIcon(processing, icon);
    const classNameValue = classNames({
        'ui-button': true,
        [`ui-button--action-${action}`]: true,
        [`ui-button--variant-${variant}`]: true,
        [`ui-button--size-${size}`]: true,
        'ui-button--icon-only': iconOnly,
        'ui-button--processing': processing,
        [className]: Boolean(className),
    });

    let button: ReactElement<{ ref?: Ref<HTMLElement> }>;

    if ('href' in props && props.href !== undefined) {
        const { href, onClick, ref, ...anchorRest } = rest as Omit<
            ButtonLinkProps,
            keyof ButtonCommonProps | 'children' | 'disabled' | 'className' | 'title' | 'aria-label'
        >;
        const inactive = disabled || processing;

        const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
            if (inactive) {
                e.preventDefault();
                return;
            }
            onClick?.(e);
        };

        const anchorProps = {
            ...anchorRest,
            ref,
            'aria-disabled': inactive || undefined,
            'aria-label': iconOnly ? title : ariaLabel,
            className: classNameValue,
            onClick: handleClick,
            tabIndex: inactive ? -1 : anchorRest.tabIndex,
        };

        button = inactive ? (
            <a {...anchorProps}>
                {leadingIcon}
                {children}
            </a>
        ) : (
            <Link
                {...anchorProps}
                href={href}>
                {leadingIcon}
                {children}
            </Link>
        );
    } else {
        const {
            type = 'button',
            ref,
            ...buttonRest
        } = rest as Omit<
            ButtonButtonProps,
            keyof ButtonCommonProps | 'children' | 'disabled' | 'className' | 'title' | 'aria-label'
        >;

        button = (
            <button
                {...buttonRest}
                ref={ref}
                type={type}
                disabled={disabled || processing}
                aria-label={iconOnly ? title : ariaLabel}
                className={classNameValue}>
                {leadingIcon}
                {children}
            </button>
        );
    }

    if (!title) {
        return button;
    }

    return <Tooltip content={title}>{button}</Tooltip>;
}

Button.displayName = 'Button';

export default Button;

export type ButtonSplitProps = Omit<ButtonCommonProps, 'variant'> &
    ButtonWithText &
    Omit<ComponentPropsWithRef<'button'>, 'children' | 'title'> & {
        onClickSplit?: (ev: MouseEvent<HTMLButtonElement>) => void;
        splitContent?: ReactNode;
        ref?: Ref<HTMLButtonElement>;
    };

export function ButtonSplit({
    onClickSplit,
    action = 'primary',
    icon,
    processing = false,
    size = 'large',
    splitContent,
    children,
    disabled = false,
    className = '',
    type = 'button',
    title,
    onClick,
    ref,
    ...rest
}: ButtonSplitProps) {
    const isDisabled = disabled || processing;
    const leadingIcon = renderLeadingIcon(processing, icon);
    const buttonTone = {
        'ui-button': true,
        [`ui-button--action-${action}`]: true,
        'ui-button--variant-filled': true,
        [`ui-button--size-${size}`]: true,
        'ui-button--processing': processing,
    };

    const group = (
        <div
            role='group'
            className={classNames({
                'ui-button-split': true,
                [`ui-button-split--size-${size}`]: true,
                [className]: Boolean(className),
            })}>
            <button
                {...rest}
                ref={ref}
                type={type}
                disabled={isDisabled}
                onClick={onClick}
                className={classNames({
                    ...buttonTone,
                    'ui-button--split-main': true,
                })}>
                {leadingIcon}
                <span className='ui-button__split-label'>{children}</span>
            </button>
            <button
                type='button'
                disabled={isDisabled}
                onClick={onClickSplit}
                aria-label='More options'
                className={classNames({
                    ...buttonTone,
                    'ui-button--split-trigger': true,
                })}>
                <Divider
                    orientation='Vertical'
                    className='ui-button__divider'
                />
                {splitContent ?? (
                    <Icon
                        icon={RiArrowDropDownLine}
                        size={16}
                        className='ui-button__split-icon'
                        aria-hidden
                    />
                )}
            </button>
        </div>
    );

    if (!title) {
        return group;
    }

    return <Tooltip content={title}>{group}</Tooltip>;
}

ButtonSplit.displayName = 'ButtonSplit';
